import { COLLISION_GROUPS } from '../../constants/Constants';
import { CharacterAttackFactory } from './attacks/CharacterAttackFactory';
import { CharacterBase, JUMP_FORCE } from './CharacterBase';
import { CharacterStateMachineHero } from './statemachines/CharacterStateMachine';

const COMBO_ATTACK = 0.25;

const COOLDOWN_GRABS = 2.5;
const THROW_ATTACK = {
  name: 'throw',
  type: 'throw',
  damage: 30,
  animation: 'throw',
  cooldown: 1,
};

export class CharacterHero extends CharacterBase {
  constructor(...args) {
    super(...args);

    this._type = 'hero';

    this._comboCooldown = 0;
    this._comboIndex = 0;
    this._grabTarget = false;
    this._cooldownGrab = 0;
    this._inputDirection = { x: 0, y: 0 };
    this._weapon = false;
    this._specialPoints = 0;
    this._lastAttackTarget;

    this.bodyCollider.collisionGroup = COLLISION_GROUPS.HERO_BODY;
    this.attackCollider.collisionGroup = COLLISION_GROUPS.HERO_ATTACK;
    this.bodyCollider.setCollidesWith([
      COLLISION_GROUPS.ENEMY_ATTACK,
      COLLISION_GROUPS.PICKUP,
      COLLISION_GROUPS.ENEMY_BODY,
      COLLISION_GROUPS.COIN,
    ]);
    this.attackCollider.setCollidesWith([
      COLLISION_GROUPS.ENEMY_BODY,
      COLLISION_GROUPS.DESTRUCTIBLE,
      COLLISION_GROUPS.PROJECTILE_ENEMY,
    ]);
  }

  handleSpineEvent(t, e) {
    console.log('handleSpineEvent', e.data.name + '--');
    switch (e.data.name) {
      case 'pickup':
        this.handlePickupItem();
        break;
      default:
        super.handleSpineEvent(t, e);
        break;
    }
  }

  get hasWeapon() {
    return this._weapon ? true : false;
  }

  get weapon() {
    return this._weapon;
  }

  damageWeapon() {
    this._weapon.uses--;
    if (this._weapon.uses <= 0) {
      this._weapon = null;
      this.setSpriteSkin('normal');
    }
  }

  addWeapon(weapon) {
    this._weapon = {
      ...weapon,
    };
    this.setSpriteSkin(this._weapon.skin);
  }

  createStateMachine() {
    return new CharacterStateMachineHero(this.scene, this);
  }

  get godMode() {
    return this.scene.game.model.godmode;
  }

  get isInvincible() {
    return this.scene.game.model.godmode;
  }

  get specialPercent() {
    return this.godMode ? 1 : this._specialPoints / 100;
  }

  adjustSpecialPoints(points) {
    if (this.godMode) this._specialPoints = 100;
    else this._specialPoints = Math.max(0, Math.min(100, this._specialPoints + points));
  }

  respawn() {
    if (this.isDead) {
      this._health = this._config.health;
      this.sprite.y = -1500;
      this.sprite.alpha = 1;
      this.bodyCollider.enabled = false;
      this.playAnimation(CharacterBase.ANIMATIONS.IDLE, true);
      this.setState(CharacterBase.STATES.RESPAWN);
    }
  }

  changeHero(config) {
    this._config = config;
    this._sprite.destroy();
    this._sprite = this.scene.add.spine(0, 0, config.spineid, CharacterBase.ANIMATIONS.IDLE, true);
    this._spriteSkins = this._sprite.getSkinList ? this._sprite.getSkinList() : [];
    this._spriteAnimations = this._sprite.getAnimationList ? this._sprite.getAnimationList() : [];
    this.setSpriteSkin('normal');
    this.add(this._sprite);
    this._health = config.health;
    this._speedMove = config.speed || 300;
  }

  get canGrab() {
    return this.isGrounded && !this._isAttacking && !this._grabTarget;
  }

  damage(...args) {
    if (this.isInvincible) return;
    super.damage(...args);
  }

  get isGrabbing() {
    return this._grabTarget ? true : false;
  }

  getGrabTarget() {
    return this._grabTarget;
  }

  setDirection({ x, y }) {
    this._inputDirection.x = x;
    this._inputDirection.y = y;
  }

  jump() {
    this.requestJump();
  }

  attack() {
    this.requestAttack();
  }

  special() {
    if (this.isJumping) return;
    this.requestAttack({ special: true, input: this._inputDirection.x });
  }

  get lives() {
    return this._lives;
  }

  looseLife() {
    this._lives--;
    return this._lives;
  }

  getInputVelocity() {
    return {
      x: this._inputDirection.x * this._speedMove,
      y: this._inputDirection.y * this._speedMove,
    };
  }

  updateComboCooldown(delta) {
    if (this._comboCooldown > 0) this._comboCooldown = Math.max(0, this._comboCooldown - delta);
  }

  // =============================================================================================
  // GRABBING
  // =============================================================================================

  checkForGrab(delta) {
    if (this._inputDirection.x === 0 && this._inputDirection.y === 0) return;
    // if we are not attack, check if we can grab a target
    if (this.canGrab) {
      this.findGrabTarget();
    } else if (this._cooldownGrab > 0) {
      this._cooldownGrab -= delta;
    }
  }

  findGrabTarget() {
    if (this._grabTarget) return;
    const enemyCollisions = this.bodyCollider.getCollisions(COLLISION_GROUPS.ENEMY_BODY);
    for (let side, e, i = 0; i < enemyCollisions.length; i++) {
      e = enemyCollisions[i];
      side = e.x >= this.x ? 1 : -1;
      if (side === -this.faceDirection && e.gameObject?.grabbable) {
        this.grabTarget(e.gameObject);
        break;
      }
    }
  }

  grabTarget(target) {
    if (!target) return;
    this._grabTarget = target;
    target.x = this.bodyCollider.x + this.bodyCollider.width * this.faceDirection;
    target.y = this.y - 2;
    target.faceDirection = -this.faceDirection;
    target.restrain();
    this.setState(CharacterBase.STATES.GRAB);
    this.playAnimation(CharacterBase.ANIMATIONS.GRAB);
  }

  releaseGrabTarget() {
    if (this._grabTarget) {
      this._grabTarget.release();
      this._grabTarget = null;
      this._cooldownGrab = COOLDOWN_GRABS;
      if (this._attackActive) this._attackActive = null;
      this.setIdleState();
    }
  }

  // =============================================================================================
  //  CHOOSING ATTACKS
  // =============================================================================================

  triggerAttack() {
    if (!this.attemptPickup()) super.triggerAttack();
  }

  chooseAttack() {
    let attack = false;

    if (this._comboCooldown <= 0) this._comboIndex = 0;
    // if character is jumping, them choose from jumping options

    if (this._requestAttack.special && !this.isJumping) {
      // trigger a special attack
      attack = this.chooseSpecialAttack(this._requestAttack.input);
    } else if (this.isJumping) {
      this._comboIndex = 0;
      attack = this.chooseJumpAttack();
      attack.earnSpecialPoints = true;
    } else if (this._weapon) {
      attack = {
        animation: this._weapon.animation,
        damage: this._weapon.damage,
        knockback: this._weapon.knockback,
        stagger: true,
      };
      this._comboIndex = 0;
    }
    // if character is grabbing then choose from grab options
    else if (this.isGrabbing) {
      if (this._inputDirection.x != 0) {
        this._comboIndex = 0;
        this.throwGrabTarget();
      } else {
        // player is grabbing a target, choose from grab attack options
        if (this._comboIndex >= this._config.grabAttacks.length) {
          attack = this.chooseGrabAttackFinisher();
          this._comboIndex = 0;
        } else {
          attack = this.chooseGrabAttack();
          attack.earnSpecialPoints = true;
          this._comboIndex++;
        }
      }
    } else {
      // no special actions are taking place, choose from generic options
      if (this._comboIndex >= this._config.basicAttacks.length) {
        this.releaseGrabTarget();
        attack = this.chooseBasicAttackFinisher();
        this._comboIndex = 0;
      } else {
        attack = this.chooseBasicAttack();
        attack.earnSpecialPoints = true;
        this._comboIndex++;
      }
    }
    // return chosen result
    if (attack) {
      return CharacterAttackFactory.create(this.scene, this, attack);
    }
    return false;
  }

  throwGrabTarget() {
    this._grabTarget.damage(THROW_ATTACK.damage);
    this._grabTarget.throw(-this.faceDirection);
    this.releaseGrabTarget();
    this.setState(CharacterBase.STATES.THROW);
    this.playAnimation(CharacterBase.ANIMATIONS.THROW, false, this.setIdleState, this);
  }

  chooseSpecialAttack(direction) {
    if (
      direction &&
      (this._specialPoints >= this._config.specialAttackForward.specialPoints || this.godMode)
    ) {
      return this._config.specialAttackForward;
    }

    if (this._specialPoints >= this._config.specialAttackStanding.specialPoints || this.godMode) {
      return this._config.specialAttackStanding;
    }
  }
  chooseJumpAttack() {
    if (this._inputDirection.x) return this._config.arialAttacksForward;

    return this._config.arialAttacksStanding;
  }

  chooseBasicAttack() {
    return this._config.basicAttacks[this._comboIndex % this._config.basicAttacks.length];
  }

  chooseBasicAttackFinisher() {
    const activeFinisher = this.scene.game.player.getActiveComboFinsiherId(this.id);
    if (activeFinisher) {
      return this._config.attackFinishers.find((f) => f.id === activeFinisher);
    }

    return this._config.attackFinishers[0];
  }

  chooseGrabAttack() {
    if (this._inputDirection.x != 0) {
      return THROW_ATTACK;
    } else return this._config.grabAttacks[this._comboIndex % this._config.grabAttacks.length];
  }

  chooseGrabAttackFinisher() {
    return this._config.grabFinishers[
      Math.floor(Math.random() * this._config.grabFinishers.length)
    ];
  }

  setTargetHitLast(target) {
    this._lastAttackTarget = target && !target.isBoss ? target : this._lastAttackTarget;
  }

  getLastAttackTarget() {
    if (this._lastAttackTarget?.isFullyDead) return null;
    return this._lastAttackTarget;
  }

  handleAttackComplete(opts) {
    if (this.isInState('demoAttack') || this.isInState('demoSpecial')) return this.demoIdle();
    this._comboCooldown = COMBO_ATTACK;
    super.handleAttackComplete();
  }

  setIdleState() {
    if (this._grabTarget) {
      this.setState(CharacterBase.STATES.GRAB);
    } else super.setIdleState();
  }

  updatePath(delta) {
    // if (this._path) super.updatePath(delta);
  }

  update(delta) {
    super.update(delta);
    this.checkCollectables();
  }

  checkCollectables() {
    const collect = this.bodyCollider.getCollisions(COLLISION_GROUPS.COIN);
    if (collect?.length) {
      for (let i = 0; i < collect.length; i++) {
        collect[i].gameObject.collect();
      }
    }
  }

  attemptPickup() {
    if (this.isGrounded && !this.isGrabbing) {
      const collisions = this.bodyCollider.getCollisions(COLLISION_GROUPS.PICKUP);
      if (collisions?.length) {
        const item = collisions[0].gameObject;
        if (item?.pickup) {
          this._pickupItem = item;
          this.setState(CharacterBase.STATES.PICKUP);
          this.playAnimation(CharacterBase.ANIMATIONS.PICKUP, false, this.setIdleState, this);
        }

        return true;
      }
    }
    return false;
  }

  handlePickupItem() {
    if (this._pickupItem) {
      this._pickupItem.pickup();
      this._pickupItem = null;
    }
  }

  demoAttack() {
    const attack = this._config.basicAttacks[0];
    this.activateAttack(CharacterAttackFactory.create(this.scene, this, attack));
    this.setState('demoAttack');
  }

  demoWalk() {
    this.playAnimation(CharacterBase.ANIMATIONS.WALK, true);
    this.setState('demoWalk');
  }

  demoIdle() {
    this.playAnimation(CharacterBase.ANIMATIONS.IDLE, true);
    this.setState('demoIdle');
  }

  demoSpecial() {
    const attack = this._config.specialAttackStanding;
    this.activateAttack(CharacterAttackFactory.create(this.scene, this, attack));
    this.setState('demoSpecial');
  }

  demoJump() {
    this.triggerJump(true);
    this.setState('demoJump');
  }

  demoPickup() {
    const pickup = this.scene.add.image(0, 0, 'game_graphics', 'pickup_food_pizza');
    this.add(pickup);
    this.scene.time.addEvent({
      delay: 150,
      callback: () => pickup.destroy(),
    });
    this.setState('demoPickup');
    this.playAnimation(CharacterBase.ANIMATIONS.PICKUP, false, this.setIdleState, this);
  }

  triggerJump(...args) {
    this.releaseGrabTarget();
    return super.triggerJump(...args);
  }

  updatePosition(velocity) {
    // update horizontal movement if applies
    if (velocity.x != 0) {
      const dirX = velocity.x > 0 ? 1 : -1;
      if (this._collisionmap.canMoveHorizontal(this.x, this.y, dirX)) {
        this.x += velocity.x;
      }
    }

    // update depth movement if applies
    if (velocity.y != 0) {
      const dirY = this._velocity.y > 0 ? 1 : -1;
      if (this._collisionmap.canMoveVertical(this.x, this.y, dirY)) {
        this.y += velocity.y;
      }
    }
  }

  setState(state) {
    if (this.isInState(CharacterBase.STATES.PICKUP)) this._pickupItem = null;
    super.setState(state);
  }
}
